/**
 * @file UI helpers — when Characterization Factor is auto-calculated vs user-entered.
 */

import type { FormDataResponse, FormField } from "../../../../../interfaces/formInterfaces";
import type { VehicleCategoryEmissionFactorPublic } from "../../../../../services/vehicleCategoryEmissionFactorsPublicService";
import { findVehicleCategoryEmissionFactor } from "../../../../../utils/a2TransportCarbonCalculations";
import { discoverA3FuelFieldNames } from "../A3/utils/a3FuelMixAutofill";
import {
  discoverA3CarbonCharacterizationFields,
  pickRepresentativeA3FuelFlatIndexForCarbonInProcess,
} from "../A3/utils/a3CarbonCharacterizationFactor";

const A1_FUEL_CODE = "A1_FUEL_EXTRACTION";
const A2_VEHICLE_CODE = "A2_VEHICLE_DETAILS";
const A3_FUEL_CODE = "A3_PROCESS_FUEL_DETAILS";

function sumPerMaterialCounts(counts: number[], endExclusive: number): number {
  let s = 0;
  for (let i = 0; i < endExclusive; i++) {
    s += Math.max(0, counts[i] ?? 0);
  }
  return s;
}

export const A1_EMBODIED_CARBON_SECTION = "A1_EMBODIED_CARBON";
export const A2_EMBODIED_CARBON_SECTION = "A2_EMBODIED_CARBON";
export const A3_EMBODIED_CARBON_SECTION = "A3_EMBODIED_CARBON_DETAILS";

export function isCharacterizationFactorValueField(field: FormField): boolean {
  const n = String(field.name ?? "").toLowerCase();
  if (field.type === "UNIT") return false;
  if (!n.includes("characterization") || !n.includes("factor")) return false;
  if (n.includes("unit")) return false;
  if (
    n.includes("reference") ||
    n.includes("functional") ||
    n.includes("rawmaterial")
  ) {
    return false;
  }
  return true;
}

export function isCharacterizationFactorUnitField(field: FormField): boolean {
  const n = String(field.name ?? "").toLowerCase();
  if (!n.includes("characterization")) return false;
  if (n.includes("unit") && n.includes("factor")) return true;
  if (field.type === "UNIT" && n.includes("factor")) return true;
  return false;
}

export function isEmbodiedCarbonCharacterizationFactorField(
  field: FormField,
  sectionCode?: string,
  formData?: FormDataResponse | null
): boolean {
  const name = String(field.name ?? "");

  if (sectionCode === A1_EMBODIED_CARBON_SECTION) {
    if (
      name === "characterizationFactor_extraction" ||
      name === "characterizationFactor_unit_extraction"
    ) {
      return true;
    }
  }

  if (sectionCode === A2_EMBODIED_CARBON_SECTION) {
    if (name === "characterizationFactor" || name === "characterizationFactor_unit") {
      return true;
    }
  }

  if (sectionCode === A3_EMBODIED_CARBON_SECTION && formData) {
    const discovered = discoverA3CarbonCharacterizationFields(formData);
    if (discovered) {
      if (name === discovered.valueField) return true;
      if (discovered.unitField && name === discovered.unitField) return true;
    }
  }

  return (
    isCharacterizationFactorValueField(field) ||
    isCharacterizationFactorUnitField(field)
  );
}

/** Unit half of an embodied-carbon CF pair (value uses the complementary check). */
export function isEmbodiedCarbonCharacterizationFactorUnitField(
  field: FormField,
  sectionCode?: string,
  formData?: FormDataResponse | null
): boolean {
  const name = String(field.name ?? "");

  if (sectionCode === A1_EMBODIED_CARBON_SECTION) {
    return name === "characterizationFactor_unit_extraction";
  }
  if (sectionCode === A2_EMBODIED_CARBON_SECTION) {
    return name === "characterizationFactor_unit";
  }
  if (sectionCode === A3_EMBODIED_CARBON_SECTION && formData) {
    const discovered = discoverA3CarbonCharacterizationFields(formData);
    return Boolean(discovered?.unitField && name === discovered.unitField);
  }

  return isCharacterizationFactorUnitField(field);
}

export type CharacterizationFactorLockContext = {
  values: Record<string, string>;
  rowIndex: number;
  formData?: FormDataResponse | null;
  fuelsPerProcess?: number[];
  processRowCount?: number;
  vehiclesPerRawMaterial?: number[];
  vehicleCategoryEmissionFactors?: VehicleCategoryEmissionFactorPublic[];
};

/** True when CF value/unit should be read-only (auto-calculated). */
export function shouldDisableAutoCalculatedCharacterizationFactor(
  sectionCode: string,
  field: FormField,
  ctx: CharacterizationFactorLockContext
): boolean {
  if (
    sectionCode === A1_EMBODIED_CARBON_SECTION ||
    sectionCode === A2_EMBODIED_CARBON_SECTION ||
    sectionCode === A3_EMBODIED_CARBON_SECTION
  ) {
    return isEmbodiedCarbonCharacterizationFactorField(
      field,
      sectionCode,
      ctx.formData
    );
  }

  return false;
}

/** A1: auto from fuel mix on the same row index. */
export function isA1CharacterizationFactorAutoCalculated(
  values: Record<string, string>,
  rowIndex: number,
  fuelCode = A1_FUEL_CODE
): boolean {
  const mix = String(
    values[`fuelMix_extraction_${fuelCode}_row${rowIndex}`] ?? ""
  ).trim();
  return mix !== "";
}

/** A2: auto from vehicle category when one vehicle per raw material. */
export function isA2CharacterizationFactorAutoCalculated(
  values: Record<string, string>,
  materialIndex: number,
  vehiclesPerRawMaterial: number[],
  vehicleCategoryEmissionFactors: VehicleCategoryEmissionFactorPublic[]
): boolean {
  const nVehicles = Math.max(1, vehiclesPerRawMaterial[materialIndex] ?? 1);
  if (nVehicles > 1) return false;
  if (!vehicleCategoryEmissionFactors.length) return false;

  const vStart = sumPerMaterialCounts(vehiclesPerRawMaterial, materialIndex);
  const cat = String(
    values[`vehicleCategory_${A2_VEHICLE_CODE}_row${vStart}`] ?? ""
  ).trim();
  if (!cat) return false;

  return Boolean(
    findVehicleCategoryEmissionFactor(cat, vehicleCategoryEmissionFactors)
  );
}

/** A3: auto from representative fuel mix in the sub-process row. */
export function isA3CharacterizationFactorAutoCalculated(
  formData: FormDataResponse | null,
  values: Record<string, string>,
  processRowIndex: number,
  fuelsPerProcess: number[],
  processRowCount: number,
  fuelCode = A3_FUEL_CODE
): boolean {
  if (!formData) return false;
  const fuelNames = discoverA3FuelFieldNames(formData);
  if (!fuelNames.mix) return false;

  const P = Math.max(1, processRowCount);
  const repFlat = pickRepresentativeA3FuelFlatIndexForCarbonInProcess(
    values,
    fuelNames,
    fuelsPerProcess,
    processRowIndex,
    P
  );
  if (repFlat == null) return false;

  const mix = String(
    values[`${fuelNames.mix}_${fuelCode}_row${repFlat}`] ?? ""
  ).trim();
  return mix !== "";
}

export const CF_AUTO_CALC_TITLE =
  "Auto-calculated from fuel mix / vehicle category — not editable";
